import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { APP_ENVIRONMENT } from '@shared/providers/environment.provider';
import {
  downloadFile,
  getFilenameFromContentDisposition,
} from '@shared/utils/download-file';
import { mimeTypes } from '@shared/constants/mime-types';
import { Environment } from '../../types/environment';
import { InvoiceDto, ResolutionStrategy } from '../../types/invoice';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  readonly API = `${this.env.apiUrl}/invoice`;

  constructor(
    @Inject(APP_ENVIRONMENT) private readonly env: Environment,
    private readonly http: HttpClient,
  ) {}

  handleAccountingInvoices(files: File[]) {
    const formData = new FormData();
    files.forEach(file => {
      const fileKey = `files`;
      formData.append(fileKey, file);
    });
    return this.http.post(`${this.API}/handleAccounting`, formData);
  }

  exportToXlsxByGroupId(groupId: number) {
    return this.http
      .get(`${this.API}/exportToXlsxByGroupId`, {
        params: { groupId },
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap(res => {
          if (res.body) {
            const name = getFilenameFromContentDisposition(
              res.headers.get('content-disposition'),
            );
            downloadFile({
              data: res.body,
              type: mimeTypes.EXCEL,
              name,
            });
          }
        }),
      );
  }

  exportToXlsxById(id: number) {
    return this.http
      .get(`${this.API}/exportToXlsxById`, {
        params: { id },
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap(res => {
          if (res.body) {
            const name = getFilenameFromContentDisposition(
              res.headers.get('content-disposition'),
            );
            downloadFile({
              data: res.body,
              type: mimeTypes.EXCEL,
              name,
            });
          }
        }),
      );
  }

  getAllInvoices(params: {
    resolutionStrategy: ResolutionStrategy;
  }): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(`${this.API}/getAll`, { params });
  }

  getById(id: number) {
    return this.http.get<InvoiceDto>(`${this.API}/getById`, {
      params: {
        id,
      },
    });
  }

  extractFromPdf(file: File): Observable<InvoiceDto> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<InvoiceDto>(`${this.API}/extractFromPdf`, formData);
  }

  saveInvoice(invoice: InvoiceDto): Observable<InvoiceDto> {
    return this.http.post<InvoiceDto>(`${this.API}/save`, invoice);
  }

  getSourceFile(id: number) {
    return this.http
      .get(`${this.API}/getSourceFile`, {
        responseType: 'blob',
        observe: 'response',
        params: {
          id,
        },
      })
      .pipe(
        tap(res => {
          if (res.body) {
            const name = getFilenameFromContentDisposition(
              res.headers.get('content-disposition'),
            );
            downloadFile({
              data: res.body,
              type: mimeTypes.APPLICATION_PDF,
              name,
            });
          }
        }),
      );
  }

  handle(params: {
    cargoId: number;
    attachmentId: number;
    orderId: number;
    fileName: string;
  }) {
    return this.http.post(`${this.API}/handle`, null, {
      params,
    });
  }
}
